import { Search, MapPin, X, ChevronRight, Info, Maximize2, Phone, Building2, Users } from 'lucide-react'
import { useI18n } from '../../i18n'
import { clinicianName } from '../../utils/clinicianName'
import { inviteClinicianMailto } from '../../utils/inviteClinician.js'
import { pcpAsOffice, tileInitials, widerMiles } from '../../utils/doctorsHome'

/**
 * DoctorsHomeKit — s123 (Dr. Rami 2026-10-29, canvas "Orella Doctors & Clinicians Home", boards A–D APPROVED;
 * copy `claude/ORELLA_DOCTORS_HOME_COPY_v1.md` APPROVED). The Doctors & Clinicians home's own pieces, kept out of
 * DoctorSearchKit (that kit is shared with the share step's picker, which keeps its look):
 *   DoctorsSearchCard — the name / practice search and the location in ONE card (was two boxes + a header icon);
 *   MyDoctorsStrip    — My Doctors as two tiles: the patient's PCP ("My PCP", from Profile › Insurance — the PCP
 *                       need not be on Orella, his ruling) and a doctor seen on Orella; "See all" opens My Doctors;
 *   ResultsHeader     — "Near you", the two counts and a compact List / Map switch;
 *   MapLegend         — On Orella / Other offices / Address not confirmed;
 *   NoOrellaYet       — the empty state (board C): what is true, then Widen and Invite;
 *   PcpRow            — "Your primary care" with Send a case.
 */
const NAVY = '#15354F'
const BLUE = '#46A8F9'
const LIGHT = '#A7D9FC'
const MUTE = '#5F7A8E'

function Avatar({ text, size = 40 }) {
  return (
    <span className="flex items-center justify-center flex-shrink-0 rounded-full font-bold"
      style={{ width: size, height: size, border: `2px solid ${LIGHT}`, color: BLUE, background: '#fff', fontSize: size > 40 ? 15 : 13 }}>
      {text || <Users size={16} />}
    </span>
  )
}

export function DoctorsSearchCard({ userLocation, setShowLoc, showLocation = true, searchQuery, setSearchQuery, searchInputRef, practiceSearch = false }) {
  const { t } = useI18n()
  // "Near you" alone for the device's own point; otherwise the place the patient chose (ZIP, address, home).
  const place = userLocation && !(userLocation.precise && userLocation.label === t('doctors.currentLocation', { defaultValue: 'Current location' }))
    ? (userLocation.label || '') : ''
  return (
    <div className="relative rounded-[18px] bg-white -mt-7 mb-3" style={{ border: '1px solid #E3ECF3', boxShadow: '0 8px 24px rgba(21,53,79,0.10)' }}>
      <div className="relative" data-testid="doctors-name-search">
        {/* ★ s123: "Search a name or practice" (doctors.searchNameOrPractice, approved) once the API's near search has
            answered (it matches practice names, v243_401_0); until then the box says what it searches today. */}
        <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: '#64748B' }} />
        <input
          ref={searchInputRef}
          type="text"
          enterKeyHint="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full h-12 pl-11 pr-10 rounded-t-[18px] text-[15px] bg-transparent focus:outline-none"
          style={{ color: NAVY }}
          aria-label={practiceSearch ? t('doctors.searchNameOrPractice', { defaultValue: 'Search a name or practice' }) : t('doctors.searchByName', { defaultValue: 'Search by name…' })}
          placeholder={practiceSearch ? t('doctors.searchNameOrPractice', { defaultValue: 'Search a name or practice' }) : t('doctors.searchByName', { defaultValue: 'Search by name…' })}
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} aria-label={t('common.close', 'Close')} className="absolute right-3.5 top-1/2 -translate-y-1/2 p-1" style={{ color: '#94A3B8' }}>
            <X size={16} />
          </button>
        )}
      </div>
      <div className="mx-4 h-px" style={{ background: '#EDF2F7' }} />
      {showLocation && setShowLoc ? (
        <button onClick={() => setShowLoc(true)} className="w-full flex items-center gap-2.5 px-4 py-3 text-left" data-testid="doctors-location-row">
          <MapPin size={17} style={{ color: BLUE, flexShrink: 0 }} />
          <span className="flex-1 min-w-0 truncate text-[14px]" style={{ color: NAVY }}>
            {place
              ? t('doctors.nearYouPlace', { defaultValue: 'Near you · {{place}}', place })
              : t('doctors.nearYou', { defaultValue: 'Near you' })}
          </span>
          <span className="text-[13px] font-bold flex-shrink-0" style={{ color: BLUE }}>{t('doctors.changeLocation', { defaultValue: 'Change' })}</span>
        </button>
      ) : null}
    </div>
  )
}

export function MyDoctorsStrip({ pcp, seen, onSeeAll, onPcp, onSeen }) {
  const { t } = useI18n()
  const tiles = []
  if (pcp && pcp.name) tiles.push({ key: 'pcp', name: pcp.name, initials: tileInitials(pcp), badge: t('doctors.badgeMyPcp', { defaultValue: 'My PCP' }), green: true, onTap: onPcp })
  for (const d of seen || []) {
    if (tiles.length >= 2) break
    tiles.push({ key: `d-${d.id}`, name: clinicianName(d), initials: tileInitials(d), badge: t('doctors.badgeSeenOnOrella', { defaultValue: 'Seen on Orella' }), onTap: () => onSeen(d) })
  }
  return (
    <section className="mb-4" data-testid="doctors-my-doctors-strip">
      <div className="flex items-baseline justify-between mb-2 px-0.5">
        <h2 className="text-[16px] font-extrabold" style={{ color: NAVY }}>{t('doctors.myDoctors', { defaultValue: 'My Doctors' })}</h2>
        <button onClick={onSeeAll} className="text-[13px] font-bold" style={{ color: BLUE }}>{t('doctors.seeAll', { defaultValue: 'See all' })}</button>
      </div>
      {tiles.length ? (
        <div className="flex gap-2.5">
          {tiles.map((x) => (
            <button key={x.key} onClick={x.onTap} className="flex-1 min-w-0 flex items-center gap-2.5 rounded-2xl p-2.5 text-left bg-white active:bg-blue-50" style={{ border: '1px solid #E3ECF3' }}>
              <Avatar text={x.initials} />
              <span className="min-w-0">
                <span className="block text-[13px] font-bold truncate" style={{ color: NAVY }}>{x.name}</span>
                <span className="inline-block mt-0.5 text-[10px] font-extrabold tracking-wide rounded-md px-1.5 py-0.5 uppercase"
                  style={x.green ? { background: '#D6F1D2', color: '#1F6B2A' } : { background: '#E9F8FF', color: NAVY }}>{x.badge}</span>
              </span>
            </button>
          ))}
        </div>
      ) : (
        <button onClick={onSeeAll} className="w-full flex items-center gap-3 rounded-2xl px-3.5 py-3 text-left bg-white active:bg-blue-50" style={{ border: '1px solid #E3ECF3' }}>
          <Avatar text="" />
          <span className="min-w-0 flex-1">
            <span className="block text-[13px] font-semibold" style={{ color: MUTE }}>{t('doctors.myDoctorsSub', { defaultValue: 'Your primary care, past Orella visits and favorites' })}</span>
          </span>
          <ChevronRight size={18} style={{ color: '#C8D6E2' }} />
        </button>
      )}
    </section>
  )
}

export function ResultsHeader({ orellaCount, otherCount, otherCapped, viewMode, setViewMode, showToggle }) {
  const { t } = useI18n()
  const counts = otherCapped // s124: a list at the search cap is "many", never 250
    ? t('doctors.resultsCountMany', { defaultValue: '{{orella}} on Orella · many offices nearby', orella: orellaCount })
    : otherCount === 1
    ? t('doctors.resultsCountOneOther', { defaultValue: '{{orella}} on Orella · 1 other office', orella: orellaCount })
    : t('doctors.resultsCount', { defaultValue: '{{orella}} on Orella · {{other}} other offices', orella: orellaCount, other: otherCount })
  const seg = (on) => (on
    ? { background: '#fff', color: NAVY, boxShadow: '0 1px 3px rgba(21,53,79,0.15)', fontWeight: 800 }
    : { background: 'transparent', color: '#64748B', fontWeight: 600 })
  return (
    <div className="flex items-center justify-between gap-3 mb-3" data-testid="doctors-results-header">
      <div className="min-w-0">
        <h2 className="text-[16px] font-extrabold" style={{ color: NAVY }}>{t('doctors.nearYou', { defaultValue: 'Near you' })}</h2>
        <p className="text-[12px] mt-0.5" style={{ color: '#64748B' }}>{counts}</p>
      </div>
      {showToggle && (
        <div role="group" aria-label={t('doctors.show', { defaultValue: 'Show' })} className="flex rounded-full p-[3px] flex-shrink-0" style={{ background: '#E7EEF4' }}>
          <button onClick={() => setViewMode('list')} className="rounded-full px-3.5 py-1.5 text-[13px]" style={seg(viewMode === 'list')} aria-pressed={viewMode === 'list'}>
            {t('doctors.listView', { defaultValue: 'List' })}
          </button>
          <button onClick={() => setViewMode('map')} className="rounded-full px-3.5 py-1.5 text-[13px]" style={seg(viewMode === 'map')} aria-pressed={viewMode === 'map'}>
            {t('doctors.mapView', { defaultValue: 'Map' })}
          </button>
        </div>
      )}
    </div>
  )
}

export function MapExpandButton({ onClick }) {
  const { t } = useI18n()
  return (
    <button type="button" onClick={onClick} aria-label={t('doctors.expandMap', { defaultValue: 'Expand map' })} data-cta="map-expand"
      className="absolute right-2.5 top-2.5 w-9 h-9 rounded-xl bg-white shadow grid place-items-center" style={{ color: NAVY, zIndex: 6, border: '1px solid #E7EEF4' }}>
      <Maximize2 size={16} />
    </button>
  )
}

export function MapLegend() {
  const { t } = useI18n()
  const dot = (bg, s) => <span className="inline-block rounded-full" style={{ width: s, height: s, background: bg }} />
  return (
    <div className="flex flex-wrap gap-x-3.5 gap-y-1 text-[11px] px-1 mb-3" style={{ color: '#475569' }} data-testid="doctors-map-legend">
      <span className="flex items-center gap-1.5">{dot(NAVY, 11)}{t('doctors.showOrella', { defaultValue: 'On Orella' })}</span>
      <span className="flex items-center gap-1.5">{dot(BLUE, 9)}{t('doctors.legendOtherOffices', { defaultValue: 'Other offices' })}</span>
      <span className="flex items-center gap-1.5">{dot('#F2A93B', 8)}{t('findDoctor.addressUnconfirmed', { defaultValue: 'Address not confirmed' })}</span>
    </div>
  )
}

export function NoOrellaYet({ miles, specialty, onWiden, othersCount }) {
  const { t } = useI18n()
  const next = widerMiles(miles)
  const title = !miles
    ? t('doctors.noOrellaMatch', 'No doctors connected to Orella match')
    : specialty
      ? t('doctors.noOrellaSpecialtyWithin', { defaultValue: 'No {{specialty}} doctors connected to Orella within {{n}} mi', specialty, n: miles })
      : t('doctors.noOrellaYetWithin', { defaultValue: 'No clinicians on Orella within {{n}} mi yet', n: miles })
  const invite = () => { window.location.href = inviteClinicianMailto() } // s126: the one invite e-mail (utils/inviteClinician)
  return (
    <div className="rounded-[20px] p-4 mb-1 flex flex-col gap-3" data-testid="doctors-no-orella-here" style={{ background: '#E9F8FF', border: `1.5px solid ${LIGHT}` }}>
      <div className="flex gap-3 items-start">
        <span className="w-10 h-10 rounded-full bg-white grid place-items-center flex-shrink-0" style={{ border: `1.5px solid ${LIGHT}` }}>
          <Info size={18} style={{ color: BLUE }} />
        </span>
        <span>
          <b className="block text-[15px] font-extrabold" style={{ color: NAVY }}>{title}</b>
          {othersCount > 0 && (
            <span className="block text-[13px] mt-1 leading-snug" style={{ color: '#334155' }}>
              {t('doctors.noOrellaYetBody', { defaultValue: 'You can still send your case to any office below. Orella faxes your Case Report to them.' })}
            </span>
          )}
        </span>
      </div>
      <div className="flex gap-2">
        {next && miles ? (
          <button onClick={() => onWiden(next)} className="flex-1 rounded-full py-2.5 text-[13px] font-bold bg-white" style={{ color: NAVY, border: `1.5px solid ${LIGHT}` }} data-cta="doctors-widen">
            {t('doctors.widenTo', { defaultValue: 'Widen to {{n}} mi', n: next })}
          </button>
        ) : null}
        <button onClick={invite} className="flex-1 rounded-full py-2.5 text-[13px] font-bold bg-white" style={{ color: NAVY, border: `1.5px solid ${LIGHT}` }} data-cta="doctors-invite">
          {t('doctors.inviteClinician', { defaultValue: 'Invite a clinician' })}
        </button>
      </div>
    </div>
  )
}

export function PcpRow({ pcp, onSend, heading = true }) {
  const { t } = useI18n()
  if (!pcp || !pcp.name) return null
  const office = pcpAsOffice(pcp)
  return (
    <section className="mt-5" data-testid="doctors-pcp-row">
      {heading && <h2 className="text-[12px] font-extrabold tracking-wider uppercase mb-2 px-0.5" style={{ color: '#475569' }}>{t('doctors.yourPrimaryCare', { defaultValue: 'Your primary care' })}</h2>}
      <div className="bg-white rounded-[18px] p-3.5 flex items-center gap-3" style={{ border: '1px solid #E3ECF3' }}>
        <Avatar text={tileInitials(pcp)} size={44} />
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-bold truncate" style={{ color: NAVY }}>{pcp.name}{pcp.credential ? `, ${pcp.credential}` : ''}</div>
          <div className="text-[12px] mt-0.5 truncate" style={{ color: '#475569' }}>
            {[pcp.specialty, t('doctors.fromYourInsurance', { defaultValue: 'from your insurance' })].filter(Boolean).join(' · ')}
          </div>
        </div>
        {office ? (
          <button onClick={() => onSend(office)} className="rounded-full px-3 py-2 text-[12px] font-bold text-white flex-shrink-0" style={{ background: NAVY }} data-cta="doctors-pcp-send">
            {t('doctors.sendCase', { defaultValue: 'Send case' })}
          </button>
        ) : pcp.phone ? (
          <a href={`tel:${pcp.phone}`} className="rounded-full px-3 py-2 text-[12px] font-bold flex items-center gap-1 flex-shrink-0" style={{ color: NAVY, border: '1.5px solid #D8E6F0' }}>
            <Phone size={12} /> {t('doctorProfile.callOffice', { defaultValue: 'Call office' })}
          </a>
        ) : null}
      </div>
    </section>
  )
}

/** s123 (board F): the practices a typed name matched ("Practices" heading, copy v1 lines 56–59). */
export function PracticeCards({ practices, onOpen }) {
  const { t } = useI18n()
  if (!practices || !practices.length) return null
  const mi = (m) => (Number.isFinite(m) ? (Math.round(m * 10) / 10).toFixed(1) : '?')
  return (
    <section className="mb-4" data-testid="doctors-practices">
      <h2 className="text-[12px] font-extrabold tracking-wider uppercase mb-2 px-0.5" style={{ color: '#475569' }}>{t('doctors.headingPractices', { defaultValue: 'Practices' })}</h2>
      <div className="flex flex-col gap-2.5">
        {practices.slice(0, 5).map((p) => (
          <div key={p.name} className="bg-white rounded-[18px] p-3.5 flex items-center gap-3" style={{ border: '1px solid #E3ECF3' }}>
            <span className="w-11 h-11 rounded-xl grid place-items-center flex-shrink-0" style={{ background: '#E9F8FF', color: BLUE }}><Building2 size={20} /></span>
            <span className="flex-1 min-w-0">
              <span className="block text-[14.5px] font-bold truncate" style={{ color: NAVY }}>{p.name}</span>
              <span className="block text-[12px] mt-0.5 truncate" style={{ color: '#475569' }}>
                {p.offices.length > 1
                  ? t('doctors.practiceMeta', { defaultValue: '{{n}} clinicians · {{m}} offices · nearest {{mi}} mi', n: p.clinicians, m: p.offices.length, mi: mi(p.miles) })
                  : t('doctors.practiceMetaOneOffice', { defaultValue: '{{n}} clinicians · {{mi}} mi', n: p.clinicians, mi: mi(p.miles) })}
              </span>
            </span>
            <button onClick={() => onOpen(p)} className="rounded-full px-3 py-2 text-[12px] font-bold text-white flex-shrink-0" style={{ background: NAVY }} data-cta="doctors-practice-send">
              {t('doctors.sendCase', { defaultValue: 'Send case' })}
            </button>
          </div>
        ))}
      </div>
    </section>
  )
}

export function OfficeIcon() {
  return (
    <span className="w-10 h-10 rounded-xl grid place-items-center flex-shrink-0" style={{ background: '#F1F5F9', color: BLUE }}>
      <Building2 size={18} />
    </span>
  )
}
